import { useEffect, useId, useState } from 'react';

import { Badge, Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input, SearchField, Select, Switch, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dataEHoraCurtas, diaIso, somarDias } from '@/comuns/formato';
import { proximoHorarioLivre } from '@/data/horarioLivre';
import type { HorarioLivre } from '@/data/horarioLivre';
import { dados } from '@/data/porta';
import type { ContatoParaEscolha, OpcoesDoAgendamento } from '@/data/tipos';
import { NovoContatoModal } from './contatos/NovoContatoModal';

export interface NovoAgendamentoModalProps {
    open: boolean;
    onClose: () => void;
    /** `YYYY-MM-DD`. */
    dataPadrao: string;
    /** `HH:MM`. */
    horaPadrao?: string;
    /** Paciente já escolhido: quem agenda a partir da ficha não procura de novo. */
    pacientePadrao?: { id: string; nome: string };
    /** Chamado depois de criar, para a tela recarregar a agenda. */
    onCriado?: () => void;
}

const DURACOES = [30, 60, 90, 120].map((m) => ({ value: String(m), label: `${m} minutos` }));

const SITUACOES = [
    { value: 'confirmado', label: 'Confirmado' },
    { value: 'pendente', label: 'Pendente de confirmação' },
];

type CampoComErro = 'paciente' | 'data' | 'hora' | 'procedimento' | 'profissional' | 'geral';
type Erros = Partial<Record<CampoComErro, string>>;

type SituacaoDoLivre = HorarioLivre | null | 'procurando' | 'erro';

function diasEntre(de: string, ate: string): number {
    return Math.round((new Date(`${ate}T12:00:00-03:00`).getTime() - new Date(`${de}T12:00:00-03:00`).getTime()) / 86_400_000);
}

export function NovoAgendamentoModal({ open, onClose, dataPadrao, horaPadrao = '14:30', pacientePadrao, onCriado }: NovoAgendamentoModalProps) {
    const aviso = useAviso();
    const idProcedimento = useId();
    const idProfissional = useId();
    const idData = useId();
    const idHora = useId();
    const idDuracao = useId();
    const idSala = useId();
    const idSituacao = useId();

    const [opcoes, setOpcoes] = useState<OpcoesDoAgendamento | null>(null);
    const [erroDoCadastro, setErroDoCadastro] = useState<string | null>(null);
    const [paciente, setPaciente] = useState<{ id: string; nome: string } | null>(pacientePadrao ?? null);
    const [busca, setBusca] = useState('');
    const [resultados, setResultados] = useState<ContatoParaEscolha[] | null>(null);
    const [cadastrando, setCadastrando] = useState(false);
    const [data, setData] = useState(dataPadrao);
    const [hora, setHora] = useState(horaPadrao);
    const [procedimentoId, setProcedimentoId] = useState('');
    const [profissionalId, setProfissionalId] = useState('');
    const [salaId, setSalaId] = useState('');
    const [situacao, setSituacao] = useState<'confirmado' | 'pendente'>('confirmado');
    const [teleconsulta, setTeleconsulta] = useState(false);
    const [duracao, setDuracao] = useState('60');
    const [observacoes, setObservacoes] = useState('');
    const [lembrete, setLembrete] = useState(true);
    const [repetir, setRepetir] = useState(false);
    const [erros, setErros] = useState<Erros>({});
    const [salvando, setSalvando] = useState(false);
    const [livre, setLivre] = useState<SituacaoDoLivre>(null);

    const idDoPacientePadrao = pacientePadrao?.id;
    const nomeDoPacientePadrao = pacientePadrao?.nome;

    /* Abrir é recomeçar. O formulário reaberto com o paciente de antes marcava
       o segundo agendamento no nome do primeiro. */
    useEffect(() => {
        if (!open) return;
        setPaciente(idDoPacientePadrao && nomeDoPacientePadrao ? { id: idDoPacientePadrao, nome: nomeDoPacientePadrao } : null);
        setBusca('');
        setResultados(null);
        setData(dataPadrao);
        setHora(horaPadrao);
        setSalaId('');
        setSituacao('confirmado');
        setTeleconsulta(false);
        setObservacoes('');
        setLembrete(true);
        setRepetir(false);
        setErros({});
        setOpcoes(null);
        setErroDoCadastro(null);

        let vivo = true;
        dados().opcoesDoAgendamento()
            .then((o) => {
                if (!vivo) return;
                setOpcoes(o);
                const primeiro = o.procedimentos[0];
                setProcedimentoId(primeiro?.id ?? '');
                setDuracao(String(primeiro?.duracaoMin ?? 60));
                setProfissionalId(o.profissionais[0]?.id ?? '');
            })
            .catch((e: unknown) => { if (vivo) setErroDoCadastro(e instanceof Error ? e.message : 'Não foi possível ler o cadastro.'); });
        return () => { vivo = false; };
    }, [open, dataPadrao, horaPadrao, idDoPacientePadrao, nomeDoPacientePadrao]);

    /* A busca vai ao cadastro de verdade. Texto livre não vira paciente: era
       assim que um nome digitado com um acento a menos criava um homônimo sem
       telefone. */
    useEffect(() => {
        if (!open || paciente || busca.trim().length < 2) {
            setResultados(null);
            return;
        }
        let vivo = true;
        const t = window.setTimeout(() => {
            dados().buscarContatos(busca)
                .then((r) => { if (vivo) setResultados(r); })
                .catch((e: unknown) => {
                    if (!vivo) return;
                    setResultados(null);
                    setErros((atuais) => ({ ...atuais, paciente: `A busca falhou: ${e instanceof Error ? e.message : 'tente de novo'}.` }));
                });
        }, 250);
        return () => { vivo = false; window.clearTimeout(t); };
    }, [open, busca, paciente]);

    const profissional = opcoes?.profissionais.find((p) => p.id === profissionalId);
    const nomeDoProfissional = profissional?.nome ?? '';

    /* O próximo horário livre sai da agenda e da jornada. No lugar dele havia
       uma "sugestão da IA" com texto fixo, que marcava 10/09 às 15:00 qualquer
       que fosse a agenda do dia. */
    useEffect(() => {
        if (!open || !opcoes || !nomeDoProfissional || !data) {
            setLivre(null);
            return;
        }
        let vivo = true;
        setLivre('procurando');
        const hoje = diaIso();
        const inicio = data < hoje ? hoje : data;
        Promise.all([dados().agendaDaSemana(inicio), dados().agendaDaSemana(somarDias(inicio, 7))])
            .then(([esta, proxima]) => {
                if (!vivo) return;
                /* Só até onde a agenda foi lida: além dela, "livre" seria chute. */
                const ultimoDiaLido = somarDias(proxima.inicio, 5);
                setLivre(proximoHorarioLivre({
                    agendamentos: [...esta.agendamentos, ...proxima.agendamentos],
                    horarios: opcoes.horarios,
                    profissional: nomeDoProfissional,
                    duracaoMin: Number(duracao),
                    aPartirDe: inicio,
                    agora: new Date(),
                    diasAFrente: diasEntre(inicio, ultimoDiaLido) + 1,
                }));
            })
            .catch(() => { if (vivo) setLivre('erro'); });
        return () => { vivo = false; };
    }, [open, opcoes, nomeDoProfissional, data, duracao]);

    const sujo = (paciente !== null && paciente.id !== idDoPacientePadrao)
        || busca.trim() !== '' || observacoes.trim() !== '' || repetir || !lembrete || teleconsulta;

    const limparErro = (campo: CampoComErro) => setErros((atuais) => ({ ...atuais, [campo]: undefined }));

    const criar = async (): Promise<boolean> => {
        const encontrados: Erros = {};
        if (!paciente) encontrados.paciente = 'Escolha o paciente na lista.';
        if (!data) encontrados.data = 'Informe a data.';
        if (!hora) encontrados.hora = 'Informe o horário.';
        if (!procedimentoId) encontrados.procedimento = 'Escolha o procedimento.';
        if (!profissionalId) encontrados.profissional = 'Escolha o profissional.';
        if (!paciente || Object.keys(encontrados).length > 0) {
            setErros(encontrados);
            return false;
        }

        const procedimento = opcoes?.procedimentos.find((p) => p.id === procedimentoId);
        const sala = opcoes?.salas.find((s) => s.id === salaId);
        setSalvando(true);
        try {
            await dados().criarAgendamento({
                pacienteId: paciente.id,
                pacienteNome: paciente.nome,
                data,
                hora,
                duracaoMin: Number(duracao),
                procedimentoId,
                procedimento: procedimento?.nome ?? '',
                profissionalId,
                profissional: nomeDoProfissional,
                ...(teleconsulta || !sala ? {} : { salaId: sala.id, sala: sala.nome }),
                teleconsulta,
                status: situacao,
                observacoes: observacoes.trim() || undefined,
                lembretePorWhatsapp: lembrete,
                repetirSemanalmente: repetir,
            });
            aviso({
                title: repetir ? '4 sessões agendadas' : teleconsulta ? 'Teleconsulta agendada' : 'Agendamento criado',
                description: `${paciente.nome} · ${dataEHoraCurtas(data, hora)}${teleconsulta ? ' · a sala de vídeo fica no agendamento' : ''}`,
            });
            onCriado?.();
            onClose();
            return true;
        } catch (e) {
            /* A falha fica na tela, não num console que ninguém abre. */
            setErros({ geral: e instanceof Error ? e.message : 'Não foi possível criar o agendamento.' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    return (
        <>
            <Modal
                open={open} onClose={onClose} width={560} title="Novo agendamento"
                dirty={sujo} onSave={criar} saveLabel="Criar agendamento"
                description="Paciente, procedimento e profissional vêm do cadastro."
                footer={
                    <>
                        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                        <Button onClick={() => void criar()} loading={salvando} disabled={!opcoes}>Criar agendamento</Button>
                    </>
                }
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {erroDoCadastro && (
                        <Alert tone="danger" title="O cadastro não carregou" dismissible={false}>{erroDoCadastro}</Alert>
                    )}
                    {erros.geral && <Alert tone="danger" dismissible={false}>{erros.geral}</Alert>}

                    <FormField label="Paciente" required error={erros.paciente}>
                        {paciente ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 42, padding: '0 6px 0 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-control)', background: 'var(--surface)' }}>
                                <Icon name="user-round" size={15} style={{ color: 'var(--neutral-500)' }} />
                                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{paciente.nome}</span>
                                <Button size="sm" variant="ghost" onClick={() => { setPaciente(null); setBusca(''); }}>Trocar</Button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <SearchField
                                    value={busca} width="100%" ariaLabel="Buscar paciente"
                                    placeholder="Buscar paciente por nome ou telefone"
                                    onChange={(v) => { setBusca(v); limparErro('paciente'); }}
                                />
                                {resultados && resultados.length > 0 && (
                                    <ul aria-label="Contatos encontrados" style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                                        {resultados.map((c) => (
                                            <li key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => { setPaciente({ id: c.id, nome: c.nome }); setResultados(null); }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '8px 12px', border: 0, background: 'var(--surface)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)' }}
                                                >
                                                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{c.nome}</span>
                                                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{c.telefone}</span>
                                                    {c.tipo !== 'paciente' && <Badge tone="neutral">{c.tipo === 'lead' ? 'Lead' : 'Contato'}</Badge>}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {resultados && resultados.length === 0 && (
                                    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                        Nenhum contato com “{busca.trim()}”.
                                    </p>
                                )}
                                <Button variant="link" onClick={() => setCadastrando(true)} style={{ alignSelf: 'flex-start' }}>
                                    Cadastrar novo paciente
                                </Button>
                            </div>
                        )}
                    </FormField>

                    {profissional && livre !== null && (
                        <div
                            role="status"
                            style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 13, color: 'var(--text-body)' }}
                        >
                            <Icon name="calendar-clock" size={15} style={{ color: 'var(--neutral-600)' }} />
                            {livre === 'procurando' && <span>Procurando o próximo horário livre de {profissional.nome}…</span>}
                            {livre === 'erro' && <span>A agenda não carregou, e sem ela não há horário livre para sugerir.</span>}
                            {typeof livre === 'object' && (
                                <>
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        Próximo horário livre de {profissional.nome}: <strong>{dataEHoraCurtas(livre.data, livre.hora)}</strong>
                                    </span>
                                    <Button size="sm" variant="secondary" onClick={() => { setData(livre.data); setHora(livre.hora); limparErro('data'); limparErro('hora'); }}>
                                        Usar este horário
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                    {profissional && livre === null && opcoes && data && (
                        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            {profissional.nome} não tem horário livre nas próximas duas semanas, pela jornada da clínica.
                        </p>
                    )}

                    <FormField
                        label="Tipo de atendimento" required
                        hint={teleconsulta
                            ? 'Sem sala física. A sala de vídeo é criada junto com o agendamento.'
                            : 'Ocupa sala e consome os insumos do procedimento.'}
                    >
                        <div style={{ display: 'flex', gap: 8 }}>
                            {([
                                { tele: false, rotulo: 'Presencial', icone: 'stethoscope', minutos: 60 },
                                { tele: true, rotulo: 'Teleconsulta', icone: 'video', minutos: 30 },
                            ]).map((op) => {
                                const ativo = teleconsulta === op.tele;
                                return (
                                    <button
                                        key={op.rotulo} type="button" aria-pressed={ativo}
                                        onClick={() => { setTeleconsulta(op.tele); setDuracao(String(op.minutos)); }}
                                        style={{
                                            flex: 1, display: 'flex', alignItems: 'center', gap: 9, height: 44, padding: '0 12px',
                                            borderRadius: 'var(--radius-control)', cursor: 'pointer', textAlign: 'left',
                                            border: `1.5px solid ${ativo ? 'var(--burgundy-700)' : 'var(--border-strong)'}`,
                                            background: ativo ? 'var(--surface-brand-soft)' : 'var(--surface)',
                                            color: ativo ? 'var(--text-brand)' : 'var(--neutral-700)',
                                            transition: 'var(--transition-control)',
                                        }}
                                    >
                                        <Icon name={op.icone} size={16} />
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: ativo ? 600 : 500 }}>{op.rotulo}</span>
                                            <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 11.5, color: ativo ? 'var(--burgundy-600)' : 'var(--text-tertiary)' }}>{op.minutos} min padrão</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </FormField>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <FormField label="Procedimento" required htmlFor={idProcedimento} error={erros.procedimento}>
                            <Select
                                id={idProcedimento} value={procedimentoId} disabled={!opcoes}
                                onChange={(v) => {
                                    setProcedimentoId(v);
                                    limparErro('procedimento');
                                    const escolhido = opcoes?.procedimentos.find((p) => p.id === v);
                                    if (escolhido && !teleconsulta) setDuracao(String(escolhido.duracaoMin));
                                }}
                                options={(opcoes?.procedimentos ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                            />
                        </FormField>
                        <FormField label="Profissional" required htmlFor={idProfissional} error={erros.profissional}>
                            <Select
                                id={idProfissional} value={profissionalId} disabled={!opcoes}
                                onChange={(v) => { setProfissionalId(v); limparErro('profissional'); }}
                                options={(opcoes?.profissionais ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                            />
                        </FormField>
                        <FormField label="Data" required htmlFor={idData} error={erros.data}>
                            <Input id={idData} type="date" value={data} onChange={(v) => { setData(v); limparErro('data'); }} invalid={!!erros.data} />
                        </FormField>
                        <FormField label="Horário" required htmlFor={idHora} error={erros.hora}>
                            <Input id={idHora} type="time" value={hora} onChange={(v) => { setHora(v); limparErro('hora'); }} invalid={!!erros.hora} />
                        </FormField>
                        <FormField label="Duração" htmlFor={idDuracao} hint="Alimenta o cronômetro do atendimento">
                            <Select id={idDuracao} value={duracao} onChange={setDuracao} options={DURACOES} />
                        </FormField>
                        {teleconsulta
                            ? (
                                <FormField label="Sala de vídeo" hint="Jitsi, no navegador, sem conta">
                                    <p style={{ margin: 0, minHeight: 42, display: 'flex', alignItems: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>
                                        Criada ao salvar
                                    </p>
                                </FormField>
                            )
                            : (
                                <FormField label="Sala" htmlFor={idSala}>
                                    <Select
                                        id={idSala} value={salaId} onChange={setSalaId} placeholder="Sem sala" disabled={!opcoes}
                                        options={(opcoes?.salas ?? []).map((s) => ({ value: s.id, label: s.nome }))}
                                    />
                                </FormField>
                            )}
                        <FormField label="Situação" htmlFor={idSituacao} style={{ gridColumn: '1 / -1' }}>
                            <Select
                                id={idSituacao} value={situacao} options={SITUACOES}
                                onChange={(v) => setSituacao(v === 'pendente' ? 'pendente' : 'confirmado')}
                            />
                        </FormField>
                    </div>

                    <FormField label="Observações" hint="Visível apenas para a equipe">
                        <Textarea value={observacoes} onChange={setObservacoes} rows={3} placeholder="Anotações sobre o atendimento…" />
                    </FormField>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
                        <Switch
                            checked={lembrete} onChange={setLembrete}
                            label="Lembretes por WhatsApp" description="Segue a régua de lembretes da clínica"
                        />
                        <Checkbox
                            checked={repetir} onChange={setRepetir}
                            label="Repetir semanalmente" description="Cria 4 sessões, uma por semana, a partir desta data"
                        />
                    </div>
                </div>
            </Modal>

            <NovoContatoModal
                open={cadastrando} tipo="paciente" nomeInicial={busca.trim()}
                onClose={() => setCadastrando(false)}
                onCriado={(criado) => { setPaciente(criado); setBusca(''); setResultados(null); limparErro('paciente'); }}
            />
        </>
    );
}
